import {
  useOrderedSections,
  useVisibility,
} from "@smart/bridge-intake-components-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import { Alert, Modal } from "@smart/itops-sb-design-system-dom";
import { fromEntries, jsonParseOrReturn } from "@smart/itops-utils-basic";
import {
  useCreateMatter,
  useDeleteSubmission,
  useLoadFields,
  useLoadGroups,
  useLoadSections,
  useLoadSubmission,
  useUpdateSubmission,
} from "@smart/manage-gql-client-dom";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import { PreviewResponses } from "../no-contact-modal/preview-responses";

type FlaggedSubmissionModalProps = {
  onClose: () => void;
  formUri: string;
  submissionUri: string;
  verificationDetails: string | undefined | null;
  isRefetching: boolean;
};

export const FlaggedSubmissionModal = ({
  formUri,
  submissionUri,
  verificationDetails,
  onClose,
  isRefetching,
}: FlaggedSubmissionModalProps) => {
  const [deleteSubmission, { loading: deletingSubmission }] =
    useDeleteSubmission();
  const [createMatter, { loading: creatingMatter }] = useCreateMatter();
  const [updateSubmission, { loading: updatingSubmission }] =
    useUpdateSubmission();
  const { terms } = loadLocale();
  const { publishEvent } = useAnalyticsEvent();

  const trackResolution = (action: "Approved" | "Deleted") => {
    publishEvent?.({
      category: "track",
      name: "IntakeFlaggedSubmissionResolved",
      screen: "IntakeHomepage",
      entryPoint: "IntakeHomepageWebEnquiries",
      component: "IntakeHomepageWebEnquiries",
      jiraKey: "LINK-3396",
      interactionType: "Active",
      properties: { Action: action },
    }).catch(console.error);
  };

  const submission = useLoadSubmission({ uri: submissionUri });

  const { result: sectionsResult, loading: loadingSections } = useLoadSections({
    formUri,
    submissionUri,
  });
  const { result: groupsResult, loading: loadingGroups } = useLoadGroups({
    formUri,
    submissionUri,
  });
  const { result: fieldsResult, loading: loadingFields } = useLoadFields({
    formUri,
    submissionUri,
  });

  const groups = groupsResult?.map((g) => g.values) || [];
  const sections = sectionsResult?.map((s) => s.values) || [];
  const fields = fieldsResult?.map((f) => f.values) || [];
  const responses = fromEntries(
    (submission?.result?.responses || []).map((r) => [
      r.fieldUri,
      jsonParseOrReturn(r.value),
    ]),
  );

  const visibility = useVisibility({ groups, sections, fields, responses });
  const orderedSections = useOrderedSections({
    groups,
    sections,
    fields,
    visibility,
    responses,
  });

  const loadingSubmission = loadingSections || loadingGroups || loadingFields;
  const disableModal =
    deletingSubmission ||
    creatingMatter ||
    updatingSubmission ||
    loadingSubmission ||
    isRefetching;

  const onDeleteSubmission = async () => {
    await deleteSubmission({ variables: { uri: submissionUri } });
    trackResolution("Deleted");
    onClose();
  };

  const onApprove = async () => {
    try {
      await updateSubmission({
        variables: {
          uri: submissionUri,
          formUri,
          fields: { verificationStatus: "approved" },
        },
      });
      await createMatter({ variables: { submissionUri } });
      trackResolution("Approved");
      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal
      open
      header={{
        icon: "solidTriangleExclamation",
        iconBgColor: "bg-yellow-450",
        text: `Review Flagged ${terms.enquiry.title}`,
      }}
      size="xLarge"
      onClose={onClose}
      loading={disableModal}
      variant="nonScrollable"
      footer={{
        buttons: [
          {
            text: `Delete ${terms.enquiry.title}`,
            type: "reset",
            variant: "destructiveSubtle",
            disabled: disableModal,
            onClick: onDeleteSubmission,
          },
          {
            text: "Cancel",
            type: "reset",
            variant: "secondarySubtle",
            disabled: disableModal,
            onClick: onClose,
          },
          {
            text: "Approve & Create Lead",
            variant: "primaryDefault",
            disabled: disableModal,
            onClick: onApprove,
          },
        ],
      }}
    >
      <div className="flex h-[58.3rem] overflow-y-hidden">
        <div className="flex flex-col flex-1 pr-[2.4rem] border-r border-neutral-100 gap-large">
          <PreviewResponses
            orderedSections={orderedSections}
            responses={responses}
            loading={loadingSubmission}
          />
        </div>
        <div className="flex-1 pl-[2.4rem] overflow-auto">
          <Alert
            variant="section"
            palette="warning"
            content={
              verificationDetails ||
              `This ${terms.enquiry.name} has been flagged for review.`
            }
          />
          <div className="mt-[3.2rem]">
            <p className="text-ui text-neutral-600">
              {`Review the form responses on the left and choose to approve this ${terms.enquiry.name} to create a lead, or delete it.`}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
